import { Component, inject, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { NgForm } from "@angular/forms";
import { CareplusNewCustomer } from "src/models/careplus-new-customer";
import { CareplusAgencyService } from "src/services/careplus-agency-service";
import { CareplusRequestsService } from "src/services/careplus-requests.service";
import {
  CareplusFeeCalculationService,
  FeeCalculationResult,
} from "src/services/careplus-fee-calculation.service";
import { Location } from "@angular/common";
import { FeeDetails } from "src/models/fee-details";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { DiscardPaymentDialogComponent } from "../discard-payment-dialog/discard-payment-dialog.component";

@Component({
  selector: "app-careplus-add-customers-detail",
  templateUrl: "./careplus-add-customers-detail.component.html",
  styleUrls: ["./careplus-add-customers-detail.component.css"],
})
export class CareplusAddCustomersDetailComponent implements OnDestroy {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private _careplusAgencyService = inject(CareplusAgencyService);
  private requestService = inject(CareplusRequestsService);
  private feeService = inject(CareplusFeeCalculationService);
  private feesSubject = new BehaviorSubject<FeeCalculationResult | null>(null);
  private destroy$ = new Subject<void>();

  customer = new CareplusNewCustomer();
  membersChanged = false;
  isFromCheckout = false;
  isAdmin = false;
  feeDetails: FeeDetails | null = null;
  calculatedFees: FeeCalculationResult | null = null;
  mainMember: any = {
    firstName: "",
    lastName: "",
    civilId: "",
    passportNumber: "",
    gender: "",
    dateOfBirth: "",
    relationType: "Main",
  };
  idTypeMap = new Map<any, string>();
  civilIdErrors = new Map<any, string | null>();
  validatingId = new Map<any, boolean>();

  constructor() {
    const navigation = this._router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.isFromCheckout = navigation.extras.state["fromCheckout"] === true;
      this.isAdmin = navigation.extras.state["isAdmin"] === true;
    }
  }

  ngOnInit() {
    const state = history.state;
    this.isAdmin = state?.isAdmin === true;
    this.isFromCheckout = state?.fromCheckout === true;
    const id = this._route.snapshot.paramMap.get("id");
    this.feesSubject.pipe(takeUntil(this.destroy$)).subscribe((fees) => {
      this.calculatedFees = fees;
    });

    this._careplusAgencyService.get(+id).subscribe((res) => {
      this.customer = res;
      this.setMainMember();
      this.getFeeDetails();
      if (this.customer.transaction?.members) {
        this.customer.transaction.members.forEach((member) => {
          this.initIdType(member);
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getFeeDetails() {
    this.requestService.getFeeDetails().subscribe((res) => {
      this.feeDetails = res;
      this.calculateFees();
    });
  }

  setMainMember() {
    if (this.customer?.transaction?.members) {
      const foundMain = this.customer.transaction.members.find(
        (member) => member.relationType === "Main",
      );
      if (foundMain) {
        this.mainMember = foundMain;
        this.initIdType(this.mainMember);
      }
    }
  }

  private initIdType(member: any) {
    if (!member) {
      return;
    }

    if (!this.idTypeMap.has(member)) {
      const type = member.passportNumber ? "Passport" : "CivilId";
      this.idTypeMap.set(member, type);
    }
  }

  getIdType(member: any): string {
    this.initIdType(member);
    return this.idTypeMap.get(member)!;
  }

  setIdType(member: any, type: string) {
    this.idTypeMap.set(member, type);
    this.onIdTypeChange(member);
  }

  onIdTypeChange(member: any) {
    const type = this.getIdType(member);
    if (type === "CivilId") {
      member.passportNumber = "";
    } else {
      member.civilId = "";
    }
    this.calculateFees();
  }

  getNonMainMembers() {
    return (
      this.customer?.transaction?.members?.filter(
        (member) => member.relationType !== "Main",
      ) || []
    );
  }

  back() {
    if (this.isFromCheckout && this.membersChanged) {
      const dialogRef = this.dialog.open(DiscardPaymentDialogComponent, {
        width: "420px",
        panelClass: "custom-dialog",
        data: {
          title: "Note",
          message:
            "If you have added/removed any member then you have to recalculate the amount.",
          confirmText: "",
          confirmBtn: "Proceed to Re-calculate",
        },
      });
      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this._router.navigate(["/careplus/careplus-employee-payment"], {
            state: { recalculate: true },
          });
        }
      });
    } else {
      this.location.back();
    }
  }

  calculateFees() {
    if (!this.feeDetails || !this.customer.transaction?.members) {
      this.feesSubject.next(null);
      return;
    }

    const discount = this.customer.transaction.calculatedDiscountAmount || 0;

    const fees = this.feeService.calculateFees(
      this.customer.transaction.members,
      this.feeDetails,
      discount,
    );

    this.customer.transaction.basePremium = fees.basePremium;
    this.customer.transaction.totalIssueFee = fees.totalIssueFee;
    this.customer.transaction.policyFee = fees.policyFee;
    this.customer.transaction.totalAmount = fees.totalAmount;
    this.feesSubject.next(fees);
  }

  addMember() {
    if (!this.customer.transaction) return;

    const childCount = this.customer.transaction.members.filter(
      (m) => m.relationType === "Child",
    ).length;

    if (childCount >= 8) {
      this.snackBar.open("Maximum 8 children allowed", "CLOSE", {
        duration: 3000,
      });
      return;
    }

    const newMember = {
      id: null,
      firstName: "",
      lastName: "",
      civilId: "",
      passportNumber: "",
      gender: "",
      dateOfBirth: "",
      relationType: "",
    };

    this.customer.transaction.members.push(newMember);
    this.initIdType(newMember);

    this.membersChanged = true;
    this.calculateFees();
  }

  isSpouseDisabled(memberIndex: number): boolean {
    const nonMain = this.getNonMainMembers();
    const currentMember = nonMain[memberIndex];

    if (!currentMember) {
      return false;
    }
    if (currentMember.relationType === "Spouse") {
      return false;
    }

    return this.customer.transaction.members.some(
      (m) => m !== currentMember && m.relationType === "Spouse",
    );
  }

  @ViewChild("customerForm") customerForm!: NgForm;

  onRelationTypeChange(memberIndex: number) {
    const nonMainMembers = this.getNonMainMembers();
    const member = nonMainMembers[memberIndex];
    if (member) {
      this.validateAge(
        "memberDateOfBirth" + memberIndex,
        member.dateOfBirth,
        member.relationType,
      );
    }
    this.calculateFees();
  }

  removeMember(index: number) {
    if (!this.customer.transaction?.members) {
      return;
    }

    const members = this.getNonMainMembers();
    const member = members[index];

    const actualIndex = this.customer.transaction.members.indexOf(member);

    if (actualIndex > -1) {
      this.customer.transaction.members.splice(actualIndex, 1);
      this.membersChanged = true;
      this.calculateFees();
    }
  }

  validateAge(controlName: string, dateOfBirth: any, relationType: string) {
    setTimeout(() => {
      const control = this.customerForm.form.get(controlName);
      if (control) {
        const ageError = this.getAgeValidationError(dateOfBirth, relationType);
        const currentErrors = control.errors ? { ...control.errors } : {};

        if (ageError) {
          Object.assign(currentErrors, ageError);
          control.setErrors(currentErrors);
        } else {
          delete currentErrors["underage"];
          delete currentErrors["overage"];
          control.setErrors(
            Object.keys(currentErrors).length > 0 ? currentErrors : null,
          );
        }
      }
    });
  }

  getAgeValidationError(dateOfBirth: any, relationType: string): any | null {
    if (!dateOfBirth) {
      return null;
    }

    const birthDate = new Date(dateOfBirth);
    const today = new Date();

    const age =
      today.getFullYear() -
      birthDate.getFullYear() -
      (today <
      new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate())
        ? 1
        : 0);

    if (
      (relationType === "Main" && age < 18) ||
      (relationType === "Spouse" && age < 18)
    ) {
      return { underage: true };
    }

    if (relationType === "Child" && age > 18) {
      return { overage: true };
    }

    return null;
  }
  isValidCivilNumber(civilNumber: string): boolean {
    let number = "";
    for (let i = 0; i < civilNumber.length; i++) {
      const d = civilNumber.charAt(i);
      if (d >= "0" && d <= "9") number += d;
    }
    if (number.length !== 12) return false;

    const formula = [2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    let sum = 0;
    for (let i = 0; i < formula.length; i++) {
      sum += parseInt(number.substring(i, i + 1), 10) * formula[i];
    }
    const lastDigit = parseInt(number.substring(11, 12), 10);
    return 11 - (sum % 11) === lastDigit;
  }

  isDuplicateId(id: string, self: any): boolean {
    const all = this.customer.transaction?.members || [];
    return all.some(
      (m) =>
        m !== self &&
        (m.civilId === id || m.passportNumber === id),
    );
  }

  validateCivilIdForMember(member: any) {
    const id: string = (member.civilId || "").trim();
    if (!id) {
      this.civilIdErrors.set(member, null);
      return;
    }

    // 1. Format check
    if (!this.isValidCivilNumber(id)) {
      this.civilIdErrors.set(member, "Invalid Civil ID format.");
      return;
    }

    // 2. Duplicate check
    if (this.isDuplicateId(id, member)) {
      this.civilIdErrors.set(member, "This Civil ID is already used by another member.");
      return;
    }

    // 3. API check
    this.validatingId.set(member, true);
    this.civilIdErrors.set(member, null);
    this._careplusAgencyService.validateMember({ civilId: id }).subscribe({
      next: (isValid: boolean) => {
        this.validatingId.set(member, false);
        this.civilIdErrors.set(
          member,
          isValid ? null : "This Civil ID is already registered.",
        );
      },
      error: () => {
        this.validatingId.set(member, false);
      },
    });
  }

  validatePassportForMember(member: any) {
    const id: string = (member.passportNumber || "").trim();
    if (!id) {
      this.civilIdErrors.set(member, null);
      return;
    }


    if (this.isDuplicateId(id, member)) {
      this.civilIdErrors.set(member, "This Passport Number is already used by another member.");
      return;
    }

    this.validatingId.set(member, true);
    this.civilIdErrors.set(member, null);
    this._careplusAgencyService.validateMember({ passportNumber: id }).subscribe({
      next: (isValid: boolean) => {
        this.validatingId.set(member, false);
        this.civilIdErrors.set(
          member,
          isValid ? null : "This Passport Number is already registered.",
        );
      },
      error: () => {
        this.validatingId.set(member, false);
      },
    });
  }

  getCivilIdError(member: any): string | null {
    return this.civilIdErrors.get(member) || null;
  }

  isValidatingId(member: any): boolean {
    return this.validatingId.get(member) || false;
  }

  onStartDateChange() {
    if (this.customer.transaction?.policyStartDate) {
      const startDate = new Date(this.customer.transaction.policyStartDate);
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
      
      this.customer.transaction.policyEndDate = endDate.toISOString();
    }
  }

  // ── Form validity ────────────────────────────────────────────────────────

  isFormValid(): boolean {
    // Validate Main Member explicitly
    if (
      this.mainMember &&
      this.getAgeValidationError(this.mainMember.dateOfBirth, "Main") !== null
    ) {
      return false;
    }

    // Block if any civil ID / passport has an error or is still being validated
    const allMembers = [
      this.mainMember,
      ...(this.customer.transaction?.members || []),
    ];
    for (const m of allMembers) {
      if (this.civilIdErrors.get(m)) return false;
      if (this.validatingId.get(m)) return false;
    }

    // Validate Non-Main Members
    const nonMainMembers = this.getNonMainMembers();
    if (
      nonMainMembers.some(
        (m) =>
          this.getAgeValidationError(m.dateOfBirth, m.relationType) !== null,
      )
    ) {
      return false;
    }

    return true;
  }

  updateCustomer() {
    if (!this.customer.transaction) {
      return;
    }

    if (!this.isFormValid()) {
      return;
    }

    const id = this._route.snapshot.paramMap.get("id")!;
    const t = this.customer.transaction;

    if (this.mainMember && t.members) {
      const mainIndex = t.members.findIndex((m) => m.relationType === "Main");
      if (mainIndex > -1) {
        t.members[mainIndex] = { ...t.members[mainIndex], ...this.mainMember };
      } else {
        t.members.push(this.mainMember);
      }
    }

    const payload = {
      policyStartDate: t.policyStartDate,
      policyEndDate: t.policyEndDate,
      phoneNumber: this.customer.phoneNumber,
      countryCode: this.customer.countryCode,

      basePremium: this.calculatedFees?.basePremium || 0,
      totalIssueFee: this.calculatedFees?.totalIssueFee || 0,
      policyFee: this.calculatedFees?.policyFee || 0,
      totalAmount: this.calculatedFees?.totalAmount || 0,
      amountToBePaid: this.calculatedFees?.amountToBePaid || 0,

      calculatedDiscountAmount: t.calculatedDiscountAmount || 0,
      promocode: t.promocode || "",
      promoType: t.promoType || "",
      promoPercentageAmount: t.promoPercentageAmount || 0,
      promoDiscountAmount: t.promoDiscountAmount || 0,
      promoRewardAmount: t.promoRewardAmount || 0,
      promoRewardStatus: t.promoRewardStatus || "",
      members: t.members || [],
    };

    this._careplusAgencyService.update(+id, payload).subscribe(() => {
      this.back();
    });
  }
}
